import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { go, replace } from 'react-router-redux';
import { call, put } from 'redux-saga/effects';
import { store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import {
  ApiPaginationResponse,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import { routes } from 'src/routes';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import instance, { CustomAxiosError } from '../../axios';
import { apiBaseRoutes, apiRoutes } from '../../constants/apiConstants';
import { Errors } from '../../constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeModal,
  setGlobalLoading,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import {
  addManufacturerAccount,
  changeManufacturerAccountStatus,
  editManufacturerAccount,
  editManufacturerAccountEmail,
  getManufacturerAccountDetails,
  getManufacturerAccountList,
  removeManufacturerAccount,
  setManufacturerAccountDetails,
  setManufacturerAccountList,
  setManufacturerAccountStatus,
} from '../actions/manufacturerAccountActions';
import { SyncUser } from '../models/Auth';
import { Company } from '../models/Company';
import {
  ManufacturerAccount,
  ManufacturerEntity,
} from '../models/ManufacturerAccount';
import { ModalType } from '../models/Modal';
import { AccountStatus } from '../models/ServiceAccount';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';
import { ResetPasswordHelper } from './authSaga';
import { AddCompanyHelper, EditCompanyHelper } from './companySaga';

/* Get manufacturer account list */
export function* ManufacturerGetListSaga(
  action: ReturnType<typeof getManufacturerAccountList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const paramsString = cleanQueryParams<
      TableQueryParameters<void, ManufacturerAccount>
    >(action.payload.params);

    const response = (yield instance.get(
      `${apiBaseRoutes.ADMIN}${apiRoutes.MANUFACTURER}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<ManufacturerAccount[]>>;

    yield put(setManufacturerAccountList(response?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get manufacturer helper */
function* GetManufacturerHelper(manId: string): Generator {
  const response = (yield instance.get(
    `${apiBaseRoutes.ADMIN}${apiRoutes.MANUFACTURER}/${manId}`
  )) as AxiosResponse<ApiResponse<ManufacturerAccount>>;

  return response;
}

/* Get manufacturer account details */
export function* ManufacturerAccountGetDetailsSaga(
  action: ReturnType<typeof getManufacturerAccountDetails>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield call(
      GetManufacturerHelper,
      action.payload?.manId
    )) as AxiosResponse<ApiResponse<ManufacturerAccount>>;

    yield put(setManufacturerAccountDetails(response.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Add manufacturer account */
export function* ManufacturerAddAccountSaga(
  action: ReturnType<typeof addManufacturerAccount>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  try {
    const companyResponse = (yield call(AddCompanyHelper, {
      ...action?.payload?.manufacturer?.baseCompany,
      base: true,
    })) as AxiosResponse<ApiResponse<Company>>;

    if (companyResponse) {
      const response = (yield instance.post(
        `${apiBaseRoutes.USER}${apiRoutes.CREATE}`,
        {
          eMail: action.payload?.manufacturer?.baseCompany?.eMail,
          /* TO-DO remove when BE changes settings */
          password: 'FumisWeb_12',
          title: action.payload?.manufacturer?.baseCompany?.title,
          phone: action.payload?.manufacturer?.baseCompany?.phone,
          enabled: true,
          companyId: companyResponse?.data?.data?.id,
          restricted: false,
          contactPerson: action.payload?.manufacturer?.contactPerson,
          contactEmail: action.payload?.manufacturer?.eMail,
          contactPhone: action.payload?.manufacturer?.phone,
          serviceAccountLimit:
            action?.payload?.manufacturer?.serviceAccountLimit,
          appNames: action.payload?.manufacturer?.appNames?.split(','),
        }
      )) as AxiosResponse<ApiResponse<SyncUser>>;

      yield call(ResetPasswordHelper, response?.data?.data?.id);

      yield put(
        addModal({
          title: i18next.t('service_account:account_created'),
          body: i18next.t('manufacturer_account:account_created_details', {
            title: action.payload?.manufacturer?.baseCompany?.title,
          }),
          modalType: ModalType.SUCCESS,
          icon: CheckCircleBig,
          onBackdropClose: false,
          primaryButtonText: i18next.t(
            'service_account:create_another_account'
          ),
          secondaryButtonText: i18next.t(
            'manufacturer_account:back_to_manufacturers'
          ),
          primaryAction: () => {
            store.dispatch(
              replace(routes.NEW_MANUFACTURER_ACCOUNT, { forcePush: true })
            );
            store.dispatch(go(0));
            store.dispatch(replace(routes.NEW_MANUFACTURER_ACCOUNT, null));
            store.dispatch(removeModal());
          },
          secondaryAction: () => {
            store.dispatch(removeModal());
            store.dispatch(
              replace(routes.MANUFACTURER_ACCOUNTS, { forcePush: true })
            );
          },
        })
      );
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
  }
}

/* Enable/disable manufacturer account */
export function* ManufacturerEnableDisableSaga(
  manufacturerId: string,
  enabled: boolean
): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.ADMIN}${apiRoutes.MANUFACTURER}/${manufacturerId}${
        enabled ? apiRoutes.ENABLE : apiRoutes.DISABLE
      }`
    )) as AxiosResponse<ApiResponse<ManufacturerEntity>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        title: i18next.t('service_account:account_status'),
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  }
}

/* Edit manufacturer email */
function* ManufacturerEditEmailSaga(
  manufacturerId: string,
  eMail: string
): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.ADMIN}/${manufacturerId}`,
      { eMail }
    )) as AxiosResponse<ApiResponse<ManufacturerAccount>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addError({
        actionType: editManufacturerAccountEmail.type,
        error: errorMessage,
      })
    );
  } finally {
    yield put(
      stopLoading({
        actionType: editManufacturerAccountEmail.type,
      })
    );
  }
}

/* Change manufacturer account status */
export function* ManufacturerChangeAccountStatusSaga(
  action: ReturnType<typeof changeManufacturerAccountStatus>
): Generator {
  yield put(showLoading());
  yield put(
    startLoading({
      actionType: action.type,
      id: action.payload?.manufacturer?.id,
    })
  );
  try {
    const response = (yield call(
      ManufacturerEnableDisableSaga,
      action?.payload?.manufacturer?.manufacturer?.id,
      action?.payload?.enabled
    )) as AxiosResponse<ApiResponse<ManufacturerEntity>>;

    if (response) {
      const enabled = action?.payload?.enabled;
      const manufacturer =
        action?.payload?.manufacturer?.manufacturer?.baseCompany?.title;

      yield put(
        setManufacturerAccountStatus({
          manufacturer: action.payload?.manufacturer,
          enabled: enabled,
        })
      );
      yield put(
        addSnackbar({
          type: SnackbarType.SUCCESS,
          title: enabled
            ? i18next.t('service_account:account_activated')
            : i18next.t('service_account:account_deactivated'),
          body: enabled
            ? i18next.t('manufacturer_account:manufacturer_account_activated', {
                manufacturer,
              })
            : i18next.t(
                'manufacturer_account:manufacturer_account_deactivated',
                {
                  manufacturer,
                }
              ),
          snackKey: Date.now(),
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(
      stopLoading({
        actionType: action.type,
        id: action.payload?.manufacturer?.id,
      })
    );
  }
}

/* Edit manufacturer account */
export function* ManufacturerEditAccountSaga(
  action: ReturnType<typeof editManufacturerAccount>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  yield put(
    startLoading({
      actionType: editManufacturerAccountEmail.type,
    })
  );
  try {
    if (action?.payload?.eMail) {
      const response = (yield call(
        ManufacturerEditEmailSaga,
        action?.payload?.id,
        action?.payload?.manufacturer?.baseCompany?.eMail
      )) as AxiosResponse<ApiResponse<ManufacturerAccount>>;

      // Return out of function if there is no response (meaning there was an error)
      if (!response) return;
    }

    const companyResponse = (yield call(EditCompanyHelper, {
      ...action?.payload?.manufacturer?.baseCompany,
    })) as AxiosResponse<ApiResponse<Company>>;

    if (companyResponse) {
      (yield instance.put(
        `${apiBaseRoutes.ADMIN}${apiRoutes.MANUFACTURER}/${action.payload?.manufacturer?.id}`,
        {
          contactPerson: action.payload?.manufacturer?.contactPerson,
          eMail: action.payload?.manufacturer?.eMail,
          phone: action.payload?.manufacturer?.phone,
          serviceAccountLimit:
            action?.payload?.manufacturer?.serviceAccountLimit,
          appNames: action.payload?.manufacturer?.appNames?.split(','),
          title: action?.payload?.manufacturer?.baseCompany?.title,
        }
      )) as AxiosResponse<ApiResponse<ManufacturerEntity>>;

      (yield call(
        ManufacturerEnableDisableSaga,
        action?.payload?.manufacturer?.id,
        action.payload?.enabled === AccountStatus.ACTIVATED
      )) as AxiosResponse<ApiResponse<ManufacturerEntity>>;

      const response = (yield call(
        GetManufacturerHelper,
        action.payload?.id
      )) as AxiosResponse<ApiResponse<ManufacturerAccount>>;

      yield put(setManufacturerAccountDetails(response?.data?.data));
      yield put(
        addSnackbar({
          type: SnackbarType.SUCCESS,
          title: i18next.t('common:changes_saved'),
          body: i18next.t('common:changes_saved_success'),
          snackKey: Date.now(),
        })
      );
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
    yield put(
      stopLoading({
        actionType: editManufacturerAccountEmail.type,
      })
    );
  }
}

/* Remove manufacturer account */
export function* ManufacturerRemoveSaga(
  action: ReturnType<typeof removeManufacturerAccount>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    (yield instance.delete(
      `${apiBaseRoutes.ADMIN}${apiRoutes.MANUFACTURER}/${action.payload?.manufacturer?.id}`
    )) as AxiosResponse<ApiResponse<ManufacturerEntity>>;

    yield put(removeModal());
    yield put(replace(routes.MANUFACTURER_ACCOUNTS, { forcePush: true }));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('service_account:account_removed'),
        body: i18next.t('service_account:account_removed_details', {
          account: action?.payload?.manufacturer?.baseCompany?.title,
        }),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    yield put(removeModal());
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}
